import { useEffect, useRef } from 'react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY_V3?.trim()

function RecaptchaV3Inner({ onVerify, onError, action = 'submit' }) {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const hasExecutedRef = useRef(false)
  // Use refs to avoid triggering re-renders when callbacks change
  const onVerifyRef = useRef(onVerify)
  const onErrorRef = useRef(onError)

  // Keep refs updated
  onVerifyRef.current = onVerify
  onErrorRef.current = onError

  useEffect(() => {
    // Only execute once when executeRecaptcha becomes available
    if (!executeRecaptcha || hasExecutedRef.current) {
      return
    }

    hasExecutedRef.current = true

    const verify = async () => {
      try {
        const token = await executeRecaptcha(action)
        onVerifyRef.current?.({ token })
      } catch (error) {
        onErrorRef.current?.('reCAPTCHA verification failed')
      }
    }

    verify()
  }, [executeRecaptcha, action])

  return (
    <div className="bot-protection bot-protection--recaptcha-v3">
      <div className="bot-protection__badge">
        <span className="bot-protection__icon">🛡️</span>
        <span className="bot-protection__text">
          Protected by reCAPTCHA —{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          {' · '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
        </span>
      </div>
    </div>
  )
}

export default function RecaptchaV3({ onVerify, onError }) {
  if (!SITE_KEY) {
    return (
      <div className="bot-protection bot-protection--warning">
        reCAPTCHA not configured (missing site key)
      </div>
    )
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={SITE_KEY}>
      <RecaptchaV3Inner onVerify={onVerify} onError={onError} />
    </GoogleReCaptchaProvider>
  )
}
